import React, { forwardRef, useMemo } from 'react'
import styled from 'styled-components'
import { StatusIcon, Tooltip, titleize } from '@streamr/streamr-layout'
import { TABLET, DESKTOP } from '@streamr/streamr-layout/styled'
import IconBox from './IconBox'
import Container from '~shared/Container'
import Topic from '~shared/Topic'
import useLastMessageTimestamp from '~hooks/useLastMessageTimestamp'
import { ago } from '~utils/time'
import { truncate } from '~utils/streams'
import PrestyledLink from '~shared/Link'
import Br from '~shared/Br'

const Link = styled(PrestyledLink)`
    display: inline-block;
`

const Status = styled.div`
    position: relative;
    width: 12px;

    > div {
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
        width: 12px;
        height: 12px;
    }

    @media (min-width: ${TABLET}px) {
        width: 16px;

        > div {
            width: 16px;
            height: 16px;
        }
    }
`

const Name = styled.div`
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    color: #0C009A;
    flex: 1;
`

const Description = styled.div`
    flex: 1;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    display: none;

    @media (min-width: ${DESKTOP}px) {
        display: block;
    }
`

const UpdatedAt = styled.div`
    display: none;
    white-space: nowrap;

    @media (min-width: ${TABLET}px) {
        display: block;
    }
`

const Actions = styled.div`
    height: 32px;
    white-space: nowrap;

    ${Link} {
        color: #ADADAD;
        background-color: inherit;
        width: 32px;
        height: 32px;
        padding: 0;
        margin: 0;
        border: none;
        apearance: none;
        background: none;
        border-radius: 4px;
        position: relative;
        cursor: pointer;

        svg {
            width: 32px;
            height: 32px;
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
        }

        :hover,
        :focus {
            background-color: #EFEFEF;
            color: #525252;
            outline: none;
        }

        :active {
            background-color: #D8D8D8;
        }
    }

    > * + * {
        margin-left: 8px;
    }
`

const PreviewIcon = () => (
    <svg viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
            d="M16.021 11.06h-4.5M13.771 14.04h-2.25M11.521 17.019h2.631M11.521 19.998h2.563"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
        <path
            d="M20.59 12.923V9.474a2 2 0 00-2-2H9.38a2 2 0 00-2 2v12a2 2 0 002 2h4.866"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
        />
        <path
            d="M20.072 22.9a3.451 3.451 0 100-6.903 3.451 3.451 0 000 6.902zM22.502 21.899L24.62 24"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
)

const ExploreIcon = () => (
    <svg viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
            // eslint-disable-next-line max-len
            d="M23.5 16a7.5 7.5 0 10-7.5 7.5M19.099 12.901a.5.5 0 00-.512-.12l-4.355 1.451-1.451 4.355a.5.5 0 00.632.632l4.355-1.451 1.451-4.355a.5.5 0 00-.12-.512zM16 8.5V10M21.303 10.697l-1.06 1.06M23.5 16H22M16 23.5V22M10.697 21.303l1.06-1.06M8.5 16H10M10.697 10.697l1.06 1.06M16 15.75a.25.25 0 01.25.25M15.75 16a.25.25 0 01.25-.25M16 16.25a.25.25 0 01-.25-.25M16.25 16a.25.25 0 01-.25.25"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
        <path
            d="M22.451 25.903a3.451 3.451 0 100-6.903 3.451 3.451 0 000 6.903zM24.88 24.902L27 27.003"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
)

const streams = [{
    id: 'streamr.eth/demos/helsinki-trams',
    description: 'Live GPS location data from trams in Helsinki, Finland',
    inactivityThresholdHours: 48,
}, {
    id: 'binance-streamr.eth/DATAUSDT/ticker',
    description: 'Ticker of DATA against USDT on Binance',
    inactivityThresholdHours: 48,
}, {
    id: 'streamr.eth/demos/twitter/sample',
    description: 'A realtime sample across all tweets',
    inactivityThresholdHours: 48,
}, {
    id: 'streamr.eth/metrics/network/sec',
    description: 'Aggregated metrics from Streamr Network',
    inactivityThresholdHours: 48,
}]

function getStreamActivityStatus(recentMessageTimestamp, inactivityThresholdHours = -Infinity) {
    if (new Date().getTime() - (inactivityThresholdHours * 60 * 60 * 1000) < recentMessageTimestamp) {
        return StatusIcon.OK
    }

    return StatusIcon.INACTIVE
}

const UnstyledStream = forwardRef(({ stream, ...props }, ref) => {
    const [timestamp, error] = useLastMessageTimestamp(stream.id)
    const { id: streamId, description } = stream

    const [path, lastPart] = useMemo(() => {
        const truncatedId = truncate(streamId)
        const lastSlashPos = truncatedId.lastIndexOf('/')

        if (lastSlashPos < 0) {
            return [undefined, truncatedId]
        }

        const a = truncatedId.slice(0, lastSlashPos)
        const b = truncatedId.slice(lastSlashPos + 1)

        return [a, b]
    }, [streamId])

    const status = error ? StatusIcon.ERROR : getStreamActivityStatus(timestamp, stream.inactivityThresholdHours)

    return (
        <div {...props} ref={ref}>
            <Status>
                <StatusIcon status={status} />
            </Status>
            <Name>
                {path !== undefined && `${path}/`}
                <strong>{lastPart}</strong>
            </Name>
            <Description>
                {description}
            </Description>
            <UpdatedAt>
                {timestamp && titleize(ago(new Date(timestamp)))}
            </UpdatedAt>
            <Actions>
                <Tooltip value="Stream data preview">
                    <Link href={`https://streamr.network/streams/${encodeURIComponent(streamId)}/preview`} newTab>
                        <PreviewIcon />
                    </Link>
                </Tooltip>
                <Tooltip value="View in Explorer">
                    <Link href={`https://streamr.network/network-explorer/streams/${encodeURIComponent(streamId)}`} newTab>
                        <ExploreIcon />
                    </Link>
                </Tooltip>
            </Actions>
        </div>
    )
})

const Stream = styled(UnstyledStream)`
    position: relative;
    background-color: #FFFFFF;
    border: 1px solid #F3F3F3;
    border-radius: 8px;
    display: flex;
    flex-direction: row;
    align-items: center;
    height: 64px;
    color: #A3A3A3;
    padding: 0 1rem;
    z-index: 0;
    font-size: 0.875rem;

    ::after {
        content: '';
        position: absolute;
        width: 100%;
        height: 100%;
        border-radius: 5px;
        box-shadow: 0px 0px 20px rgba(0, 0, 0, 0.08);
        transition: opacity 300ms ease-in-out;
        top: 0;
        left: 0;
        opacity: 0;
        z-index: -1;
    }

    :hover {
        ::after {
            opacity: 1;
        }
    }

    > * + * {
        margin-left: 1rem;
    }

    @media (min-width: ${TABLET}px) {
        padding: 0 2rem 0 1.5rem;

        > * + ${Actions} {
            margin-left: 2rem;
        }
    }

    @media (min-width: ${DESKTOP}px) {
        padding: 0 2rem 0 2.5rem;

        ${Actions} {
            opacity: 0;
            transition: opacity 300ms ease-in-out;
        }

        :hover {
            ${Actions} {
                opacity: 1;
            }
        }
    }
`

const StreamList = styled.div`
    ${Stream} + ${Stream} {
        margin-top: 1rem;
    }
`

const UnstyledStreams = (props) => (
    <Container {...props}>
        <IconBox>
            <IconBox.Body icon="stream">
                <Topic.Title>
                    Explore streams
                </Topic.Title>
                <Topic.Paragraph>
                    Real-time data streams are the atomic units of the
                    <Br tablet />
                    Streamr network.
                    <Br desktop />
                    Each stream uses an Ethereum
                    <Br tablet />
                    address or ENS name as its primary
                    <Br desktop />
                    domain.
                    <Br tablet />
                    Explore a few public examples below, via the
                    <Br tablet />
                    Network
                    <Br desktop />
                    Explorer or simply view their live data.
                </Topic.Paragraph>
            </IconBox.Body>
            <StreamList $appearIntersection $appearId="list">
                {streams.map((stream) => (
                    <Stream key={stream.id} stream={stream} $appear $appearParentId="list" />
                ))}
            </StreamList>
        </IconBox>
    </Container>
)

const Streams = styled(UnstyledStreams)`
    padding: 0 1rem;

    ${Topic.Title} {
        margin: 0;
        margin-bottom: 1rem;
        text-align: left;
    }

    ${Topic.Paragraph} {
        line-height: 1.75rem;
        font-size: 1rem;
    }

    ${IconBox} + ${IconBox} {
        margin-top: 1.5rem;
    }

    ${StreamList} {
        margin-top: 3.5rem;
    }

    @media (min-width: ${TABLET}px) {
        padding: 0;
        max-width: 44rem;

        ${StreamList} {
            margin-top: 6rem;
        }

        ${IconBox} {
            padding: 5.5rem 1.5rem 2rem;
        }

        ${IconBox.Body} {
            margin-left: 0.5rem;
        }

        ${IconBox.Icon} {
            margin-right: 3.5rem;
        }
    }

    @media (min-width: ${DESKTOP}px) {
        max-width: 69rem;

        ${StreamList} {
            margin-top: 7rem;
        }

        ${Topic.Paragraph} {
            font-size: 1.125rem;
            line-height: 1.875rem;
        }

        ${IconBox} {
            padding: 6.5rem 2.5rem 4.5rem;
        }

        ${IconBox.Body} {
            margin-left: 5rem;
        }

        ${IconBox.Icon} {
            margin-right: 5rem;
        }
    }
`

export default Streams
